/**
 * Quarterly Projects View
 * View and manage quarterly operational expense projects
 */

import React, { useState } from 'react';
import { Link } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Project, Task } from '../types';
import {
  getCurrentQuarter,
  getNextQuarter,
  getPreviousQuarter,
  getAllQuarterlyProjects,
  calculateTotalBudget,
  calculateSpentAmount,
  getBudgetUtilization,
  getQuarterStatus,
  QuarterInfo,
  generateYearQuarters,
  createQuarterlyProject,
} from '../utils/quarterlyProjects';
import { Calendar, DollarSign, TrendingUp, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { ensureCostItemCode } from '../utils/costItems';
import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
} from '../constants/widgetTheme';

interface QuarterlyProjectsViewProps {
  projects: Project[];
  tasks: Task[];
  organizationId: string;
  organizationName: string;
  onCreateProject: (project: Project) => void;
}

export default function QuarterlyProjectsView({
  projects,
  tasks,
  organizationId,
  organizationName,
  onCreateProject,
}: QuarterlyProjectsViewProps) {
  const currentQuarter = getCurrentQuarter();
  const [selectedYear, setSelectedYear] = useState(currentQuarter.year);
  
  const quarterlyProjects = getAllQuarterlyProjects(projects, organizationId);
  const yearQuarters = generateYearQuarters(selectedYear);

  const handleCreateQuarterlyProject = (quarterInfo: QuarterInfo) => {
    const newProject = createQuarterlyProject(organizationId, organizationName, quarterInfo);
    onCreateProject(newProject);
  };

  const getProjectForQuarter = (quarterInfo: QuarterInfo): Project | null => {
    return quarterlyProjects.find(
      (p) =>
        p.metadata?.quarter === `Q${quarterInfo.quarter}` &&
        p.metadata?.year === quarterInfo.year.toString()
    ) || null;
  };

  const renderQuarterCard = (quarterInfo: QuarterInfo) => {
    const project = getProjectForQuarter(quarterInfo);
    const status = getQuarterStatus(quarterInfo);
    
    if (!project) {
      return (
        <Card key={quarterInfo.label} className="border-2 border-dashed border-border">
          <CardHeader className="px-3 pt-3 pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-semibold">{quarterInfo.label}</CardTitle>
              <Badge variant="outline" className="text-xs">
                {status === 'upcoming' ? 'Upcoming' : status === 'current' ? 'Current' : 'Past'}
              </Badge>
            </div>
            <CardDescription className="text-[11px]">
              {quarterInfo.startDate.toLocaleDateString()} - {quarterInfo.endDate.toLocaleDateString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-3 pt-0 pb-3">
            <div className="py-1.5 text-center">
              <p className="mb-2.5 text-xs text-muted-foreground">No quarterly project created</p>
              {status !== 'past' && (
                <Button
                  size="sm"
                  onClick={() => handleCreateQuarterlyProject(quarterInfo)}
                  variant="outline"
                  className="h-6 px-1.5 text-[11px]"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Create {quarterInfo.label} Project
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    const totalBudget = calculateTotalBudget(project);
    const spentAmount = calculateSpentAmount(project, tasks);
    const utilization = getBudgetUtilization(totalBudget, spentAmount);
    const projectTasks = tasks.filter((t) => t.projectId === project.id);

    return (
      <Card key={quarterInfo.label} className="transition-shadow hover:shadow-md">
        <CardHeader className="px-3 pt-3 pb-1">
          <div className="flex items-center justify-between">
            <Link to={`/app/projects/${project.id}`} className="hover:underline">
              <CardTitle className="text-xs font-semibold">{quarterInfo.label}</CardTitle>
            </Link>
            <Badge
              variant={status === 'current' ? 'default' : 'outline'}
              className={`text-xs ${status === 'current' ? 'bg-chart-2/10' : ''}`}
            >
              {status === 'upcoming' ? 'Upcoming' : status === 'current' ? 'Active' : 'Completed'}
            </Badge>
          </div>
          <CardDescription className="text-[11px]">
            {quarterInfo.startDate.toLocaleDateString()} - {quarterInfo.endDate.toLocaleDateString()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 px-3 pt-0 pb-3">
          {/* Budget Overview */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Budget Utilization</span>
              <span className="font-semibold text-foreground">{utilization}%</span>
            </div>
            <Progress value={utilization} className="h-1" />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>${spentAmount.toLocaleString()} spent</span>
              <span>${totalBudget.toLocaleString()} total</span>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-0.5">
            <div className="text-[11px] font-medium text-muted-foreground">Budget Categories:</div>
            <div className="flex flex-wrap gap-1">
              {project.costs.slice(0, 1).map((cost) => (
                <span key={cost.id} className="inline-flex items-center rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-foreground">
                  {ensureCostItemCode(cost)} - {cost.category}: ${cost.amount.toLocaleString()}
                </span>
              ))}
            </div>
            {project.costs.length > 1 && (
              <div className="text-[10px] text-muted-foreground">+{project.costs.length - 1} more</div>
            )}
          </div>

          {/* Task Count */}
          <div className="flex items-center justify-between border-t pt-1">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{projectTasks.length} expenses</span>
            </div>
            <Link to={`/app/projects/${project.id}`}>
              <Button size="sm" variant="outline" className="h-6 px-1.5 text-[11px]">
                View Details
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-2.5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
            <span className="text-xl">💰</span>
            Quarterly Operational Expenses
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Track recurring expenses across quarters
          </p>
        </div>
      </div>

      {/* Year Navigation */}
      <div className="flex items-center justify-between rounded-lg bg-muted p-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedYear(selectedYear - 1)}
          className="h-7 px-2 text-xs"
        >
          <ChevronLeft className="mr-1 h-3.5 w-3.5" />
          {selectedYear - 1}
        </Button>
        
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-foreground">{selectedYear}</h3>
          {selectedYear === currentQuarter.year && (
            <Badge variant="default" className="text-[11px]">Current Year</Badge>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedYear(selectedYear + 1)}
          className="h-7 px-2 text-xs"
        >
          {selectedYear + 1}
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-3">
        <Card className={WIDGET_PANEL_CLASS}>
          <CardContent className="px-3 py-1.5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-1">
                <DollarSign className="h-3.5 w-3.5 text-primary" />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Total Budget ({selectedYear})</div>
                <div className="text-sm font-bold text-foreground">
                  $
                  {yearQuarters
                    .map((q) => getProjectForQuarter(q))
                    .filter(Boolean)
                    .reduce((sum, p) => sum + calculateTotalBudget(p!), 0)
                    .toLocaleString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={WIDGET_PANEL_CLASS}>
          <CardContent className="px-3 py-1.5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-1">
                <TrendingUp className="h-3.5 w-3.5 text-primary" />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Total Spent ({selectedYear})</div>
                <div className="text-sm font-bold text-foreground">
                  $
                  {yearQuarters
                    .map((q) => getProjectForQuarter(q))
                    .filter(Boolean)
                    .reduce((sum, p) => sum + calculateSpentAmount(p!, tasks), 0)
                    .toLocaleString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={WIDGET_PANEL_CLASS}>
          <CardContent className="px-3 py-1.5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-1">
                <Calendar className="h-3.5 w-3.5 text-primary" />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Active Projects</div>
                <div className="text-sm font-bold text-foreground">
                  {yearQuarters.filter((q) => getProjectForQuarter(q)).length} / 4
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quarter Cards */}
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {yearQuarters.map((quarter) => renderQuarterCard(quarter))}
      </div>

      {/* Quick Actions */}
      {selectedYear === currentQuarter.year && (
        <Card className={WIDGET_ALERT_INFO_CLASS}>
          <CardContent className="px-3 pt-2.5 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="mb-0.5 text-sm font-semibold text-foreground">Quick Actions</h4>
                <p className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                  Current quarter: {currentQuarter.label}
                </p>
              </div>
              <div className="flex gap-2">
                {!getProjectForQuarter(currentQuarter) && (
                  <Button
                    onClick={() => handleCreateQuarterlyProject(currentQuarter)}
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Create Current Quarter
                  </Button>
                )}
                {!getProjectForQuarter(getNextQuarter()) && (
                  <Button
                    onClick={() => handleCreateQuarterlyProject(getNextQuarter())}
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Create Next Quarter
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
